import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { CallState } from "redmond";
import { AvailabilityResultEnum } from "redmond/apis/tysons/vacation-rentals";

import {
  AVAILABLE,
  CORE_VACATION_RENTALS_SHOP,
  getExperimentVariant,
  useExperiments,
} from "../../../context/experiments";
import { PATH_VACATION_RENTALS_BOOK_CONFIRMATION } from "../../../utils/paths";
import {
  getAdultsCount,
  getChildrenCount,
  getFromDate,
  getPetsCount,
  getUntilDate,
} from "../../search/reducer";
import { fetchVacationRentalShop } from "../../shop/actions/actions";
import {
  VacationRentalShopCallState,
  getVacationRentalShopCallState,
  getVacationRentalShopSelectedListing,
} from "../../shop/reducer";
import { replaceWithShop } from "../../shop/utils/queryStringHelpers";
import { redoSearch } from "../actions/actions";
import { getLoadedStayKey, getUrlStayKey } from "./stayKey";

interface UseVacationRentalBookListingArgs {
  history: RouteComponentProps["history"];
  location: RouteComponentProps["location"];
  selectedHome: ReturnType<typeof getVacationRentalShopSelectedListing>;
}

// Loads the listing for the book URL's stay into the module store and reports
// when it's ready, so the workflows only mount on the right listing. The store
// outlives the page: without this, checkout for listing B could mount on, quote,
// and book a listing A left over from an earlier checkout.
export const useVacationRentalBookListing = ({
  history,
  location,
  selectedHome,
}: UseVacationRentalBookListingArgs) => {
  const dispatch = useDispatch();
  const expState = useExperiments();

  const isReplatformShopEnabled =
    getExperimentVariant(expState.experiments, CORE_VACATION_RENTALS_SHOP) ===
    AVAILABLE;

  const areExperimentsLoaded =
    [CallState.Success, CallState.Failed].includes(expState.callState) ||
    expState.experiments.length > 0;

  const vrShopCallState = useSelector(getVacationRentalShopCallState);
  const searchFromDate = useSelector(getFromDate);
  const searchUntilDate = useSelector(getUntilDate);
  const searchAdultsCount = useSelector(getAdultsCount);
  const searchChildrenCount = useSelector(getChildrenCount);
  const searchPetsCount = useSelector(getPetsCount);

  const isConfirmationPage = location.pathname.startsWith(
    PATH_VACATION_RENTALS_BOOK_CONFIRMATION.replace(/\/$/, "")
  );

  // redoSearch clears the book reducer and, via cancelOnRedoSearch, drops the
  // last visit's in-flight quote so it can't land here. Skipped on confirmation:
  // that booking really happened.
  useEffect(() => {
    if (!isConfirmationPage) {
      dispatch(redoSearch());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset once per book visit
  }, []);

  // "Is *this URL's* stay loaded", not "is anything loaded": a leftover listing,
  // or the right listing for old dates/guests, must not count.
  const urlStayKey = getUrlStayKey(location.search);
  const urlListingId = new URLSearchParams(location.search).get("listingId");
  const loadedStayKey = getLoadedStayKey({
    listingId: selectedHome?.listingId.id,
    fromDate: searchFromDate,
    untilDate: searchUntilDate,
    adultsCount: searchAdultsCount,
    childrenCount: searchChildrenCount,
    petsCount: searchPetsCount,
  });

  // The stay whose listing has finished loading on this page, and the stay a
  // fetch started here is loading (`undefined` = none yet).
  const [readyStayKey, setReadyStayKey] = useState<string | null>();
  const requestedStayKey = useRef<string | null>();

  // Gates mounting the workflows (a loading popup alone doesn't stop their
  // mount-time quote from running against a stale listing).
  const isListingReady =
    readyStayKey !== undefined &&
    readyStayKey === urlStayKey &&
    vrShopCallState !== VacationRentalShopCallState.InProcess &&
    selectedHome != null &&
    selectedHome.listingId.id === urlListingId;

  // Show a loading state while we (re)load the listing from the URL, so the
  // user never sees a blank or half-loaded page. Failures fall through to the
  // error modal.
  const isListingLoading =
    !isListingReady && vrShopCallState !== VacationRentalShopCallState.Failed;

  // The replatformed shop doesn't fill this module's store, so its first load on
  // this page always fetches the URL's stay. Otherwise (legacy, or a later stay
  // change such as a desktop date edit) we only fetch when the store doesn't
  // already hold the URL's stay. Runs once experiments load (they tell us which
  // flow we're in), and again if the URL moves to another stay.
  useEffect(() => {
    if (!areExperimentsLoaded) {
      return;
    }

    if (isConfirmationPage) {
      if (selectedHome == null) {
        replaceWithShop({ history });
      }
      return;
    }

    if (urlListingId == null) {
      // Nothing in the URL to check the store's listing against.
      replaceWithShop({ history });
      return;
    }

    if (
      (isReplatformShopEnabled && readyStayKey === undefined) ||
      urlStayKey == null ||
      loadedStayKey !== urlStayKey
    ) {
      requestedStayKey.current = urlStayKey;
      dispatch(
        fetchVacationRentalShop(history, {
          overrideStateByQueryParams: true,
          fetchListingCalendar: true,
        })
      );
    } else {
      setReadyStayKey(urlStayKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stay, not on every store change
  }, [areExperimentsLoaded, urlListingId, urlStayKey]);

  // If the listing can't be booked for these dates (unavailable, or the fetch
  // failed), send the user back to the shop, which shows the right messaging.
  // We only act on a fetch that settles while this page is open, so a leftover
  // cached listing never bounces a user who picked valid dates. Redirects use
  // replace, not push: pushing a shop entry made back from the shop land on
  // checkout again, and the URL carries any dates edited on checkout.
  const prevShopCallState = useRef(vrShopCallState);
  useEffect(() => {
    const prevCallState = prevShopCallState.current;
    prevShopCallState.current = vrShopCallState;

    if (prevCallState !== VacationRentalShopCallState.InProcess) {
      return;
    }

    const fetchFailed = vrShopCallState === VacationRentalShopCallState.Failed;
    const notBookable =
      vrShopCallState === VacationRentalShopCallState.Success &&
      (selectedHome == null ||
        selectedHome.availability.AvailabilityResult !==
          AvailabilityResultEnum.Available);

    if (fetchFailed || notBookable) {
      replaceWithShop({ history });
    } else if (requestedStayKey.current !== undefined) {
      // takeLatest cancels any earlier shop fetch, so this is the one we started.
      setReadyStayKey(requestedStayKey.current);
      requestedStayKey.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vrShopCallState, selectedHome]);

  return { isListingReady, isListingLoading };
};
